import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import Modal from './Modal';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import BlockTitle from './BlockTitle';
import useCompanies from '../lib/useCompanies';
import useProjectManagers from '../lib/useProjectManagers';
import './custom-bootstrap.scss';

const EditInvestmentModal = ({ isOpen, onClose, investmentId, onSave }) => {
  const { companies, loading: companiesLoading } = useCompanies();
  const { managers, loading: managersLoading } = useProjectManagers();
  const [investment, setInvestment] = useState({
    location: '',
    manager: { name: '', phone: '' },
    managerUid: '',
    total_cost: 0,
    total_size: 0,
    contractsDriveUrl: '',
  });
  const [reminders, setReminders] = useState([
    { days: 7, enabled: true },
    { days: 14, enabled: true },
    { days: 21, enabled: false },
    { days: 30, enabled: true },
  ]);

  useEffect(() => {
    if (investmentId && isOpen) {
      const fetchInvestmentAndReminders = async () => {
        // Fetch investment details
        const investmentRef = doc(firestore, `investments/${investmentId}`);
        const investmentSnap = await getDoc(investmentRef);
        if (investmentSnap.exists()) {
          const data = investmentSnap.data();
          setInvestment({
            ...data,
            manager: data.manager || { name: '', phone: '' },
            managerUid: data.managerUid || '',
            contractsDriveUrl: data.contractsDriveUrl || '',
          });
        }

        // Fetch reminder settings from separate schema
        const remindersRef = doc(firestore, `investment_reminders/${investmentId}`);
        const remindersSnap = await getDoc(remindersRef);
        if (remindersSnap.exists()) {
          const savedReminders = remindersSnap.data().reminders;
          if (savedReminders) {
            setReminders(savedReminders);
          }
        }
      };

      fetchInvestmentAndReminders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentId, isOpen]);

  // Keep handleReminderChange for future reminders UI (currently unused but
  // kept for parity with previous implementation)
  // eslint-disable-next-line no-unused-vars
  const handleReminderChange = (index, key, value) => {
    const updatedReminders = [...reminders];
    updatedReminders[index][key] = value;
    setReminders(updatedReminders);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Save investment data
    const investmentRef = doc(firestore, `investments/${investmentId}`);
    const selectedCompany = companies.find((c) => c.id === investment.company_id) || null;
    await setDoc(
      investmentRef,
      {
        ...investment,
        total_cost: parseFloat(investment.total_cost) || 0,
        total_size: parseFloat(investment.total_size) || 0,
        company_id: investment.company_id || '',
        company: selectedCompany
          ? { name: selectedCompany.name, nip: selectedCompany.nip, address: selectedCompany.address }
          : investment.company || null,
        managerUid: investment.managerUid || '',
        contractsDriveUrl: investment.contractsDriveUrl || '',
      },
      { merge: true }
    );

    // Save reminders in a separate schema
    const remindersRef = doc(firestore, `investment_reminders/${investmentId}`);
    await setDoc(remindersRef, { reminders });

    if (onSave) onSave();
    onClose();
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setInvestment((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onManagerSelect = (e) => {
    const uid = e.target.value;
    const pm = managers.find((m) => m.uid === uid);
    setInvestment((prev) => ({
      ...prev,
      managerUid: uid,
      manager: {
        ...(prev.manager || {}),
        name: pm ? pm.displayName : '',
      },
    }));
  };

  const onManagerPhoneChange = (e) => {
    const { value } = e.target;
    setInvestment((prev) => ({
      ...prev,
      manager: { ...(prev.manager || {}), phone: value },
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-8">
        <BlockTitle className="text-[22px]">
          Edytuj inwestycję
        </BlockTitle>

        <form onSubmit={handleSave} className="grid grid-cols-2 gap-6 mt-6">
          {/* Company */}
          <div className="col-span-2">
            <FormLabel htmlFor="company_id">Spolka</FormLabel>
            <select
              id="company_id"
              name="company_id"
              className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
              value={investment.company_id || ''}
              onChange={onChange}
              disabled={companiesLoading}
            >
              <option value="">
                {companiesLoading ? 'Wczytywanie spolek...' : '-- Wybierz spolke --'}
              </option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div className="col-span-2">
            <FormLabel htmlFor="location">Adres inwestycji</FormLabel>
            <FormInput
              id="location"
              name="location"
              value={investment.location}
              onChange={onChange}
              placeholder="Wprowadź adres inwestycji"
              required
            />
          </div>

          {/* Google Drive link */}
          <div className="col-span-2">
            <FormLabel htmlFor="contractsDriveUrl">Link do umów (Google Drive)</FormLabel>
            <FormInput
              id="contractsDriveUrl"
              name="contractsDriveUrl"
              type="url"
              value={investment.contractsDriveUrl || ''}
              onChange={onChange}
              placeholder="https://drive.google.com/drive/folders/..."
            />
          </div>

          {/* Project Manager dropdown */}
          <div>
            <FormLabel htmlFor="managerUid">Kierownik projektu</FormLabel>
            <select
              id="managerUid"
              name="managerUid"
              className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
              value={investment.managerUid || ''}
              onChange={onManagerSelect}
              disabled={managersLoading}
            >
              <option value="">
                {managersLoading ? 'Wczytywanie...' : '-- Wybierz kierownika --'}
              </option>
              {managers.map((m) => (
                <option key={m.uid} value={m.uid}>{m.displayName}</option>
              ))}
            </select>
          </div>

          {/* Manager Phone */}
          <div>
            <FormLabel htmlFor="phone">Numer telefonu (opcjonalnie)</FormLabel>
            <FormInput
              id="phone"
              name="phone"
              value={(investment.manager && investment.manager.phone) || ''}
              onChange={onManagerPhoneChange}
              placeholder="Wprowadź numer telefonu"
            />
          </div>

          {/* Investment Cost */}
          <div>
            <FormLabel htmlFor="total_cost">Umowa z GW (PLN)</FormLabel>
            <FormInput
              id="total_cost"
              name="total_cost"
              type="number"
              value={investment.total_cost || ''}
              onChange={onChange}
              placeholder="Wprowadź wartość umowy z GW"
            />
          </div>

          {/* Total Size */}
          <div>
            <FormLabel htmlFor="total_size">Powierzchnia całkowita (m²)</FormLabel>
            <FormInput
              id="total_size"
              name="total_size"
              type="number"
              step="0.01"
              value={investment.total_size || ''}
              onChange={onChange}
              placeholder="Wprowadź powierzchnię całkowitą"
            />
          </div>

          {/* Buttons */}
          <div className="col-span-2 flex justify-end mt-10">
            <Button type="submit" variant="default">
              Zapisz
            </Button>
            <Button type="button" variant="bordered" onClick={onClose} className="ml-4">
              Anuluj
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditInvestmentModal;
